import { AUDIO_PACKET_CONFIG, AUDIO_PACKET_FRAME } from "./audio-protocol.js";

const AUDIO_RESUBSCRIBE_MS = 800;
/** Очередь FPS для живого голоса: ~0.6–1 с при 20 мс кадрах, не секунды. */
const AUDIO_QUEUE_CAPACITY = 40;

/** FastPubSub: только звук (reconnect, диагностика). */
export class FastPubSubAudioTransport {
  constructor({ client, tenant, subjectPrefix, onAudioPacket, onAudioDiagnostic = () => {}, onWarning = console.warn }) {
    this.client = client;
    this.tenant = tenant;
    this.subjectPrefix = subjectPrefix;
    this.onAudioPacket = onAudioPacket;
    this.onAudioDiagnostic = onAudioDiagnostic;
    this.onWarning = onWarning;
    this.subscriptions = new Map();
  }

  audioChannel(subjectPrefix) {
    return `${subjectPrefix}.media.audio.opus`;
  }

  publishAudio(payload) {
    return this.client.publish(this.tenant, this.audioChannel(this.subjectPrefix), payload);
  }

  syncSubscriptions(subjectPrefixes) {
    const wanted = new Set([...subjectPrefixes].map(prefix => this.audioChannel(prefix)));
    this.sync(this.subscriptions, wanted, channel => this.subscribeAudio(channel));
  }

  /** Синхронизирует карту подписок с нужным набором каналов. */
  sync(subscriptions, wanted, subscribe) {
    for (const [channel, subscription] of subscriptions) {
      if (wanted.has(channel)) continue;
      subscription.stopped = true;
      subscriptions.delete(channel);
      this.client.unsubscribe(this.tenant, `${channel}.#`).catch(this.onWarning);
      this.onAudioDiagnostic({ type: "audio_unsubscribed", channel });
    }
    for (const channel of wanted) {
      if (!subscriptions.has(channel)) subscribe(channel);
    }
  }

  /** Один пакет из очереди подписки. */
  deliverAudioPacket(message) {
    if (message.payload[0] !== AUDIO_PACKET_CONFIG && message.payload[0] !== AUDIO_PACKET_FRAME) return;
    this.onAudioDiagnostic({
      type: message.payload[0] === AUDIO_PACKET_CONFIG ? "audio_config_packet" : "audio_frame_packet",
      channel: message.channel
    });
    this.onAudioPacket(message.channel, message.payload);
  }

  /** Пауза перед повторной подпиской после обрыва recv. */
  waitResubscribe() {
    return new Promise(resolve => setTimeout(resolve, AUDIO_RESUBSCRIBE_MS));
  }

  /**
   * Подписка на аудио с авто-reconnect, когда recv() вернул null.
   */
  async subscribeAudio(channel) {
    const subscription = { stopped: false };
    this.subscriptions.set(channel, subscription);

    while (!subscription.stopped) {
      let queue;
      try {
        queue = await this.client.subscribe(this.tenant, `${channel}.#`, { queueCapacity: AUDIO_QUEUE_CAPACITY });
        this.onAudioDiagnostic({ type: "audio_subscribed", channel });
      } catch (error) {
        if (subscription.stopped) return;
        this.onAudioDiagnostic({ type: "audio_subscription_error", channel });
        this.onWarning(`audio subscription ${channel}`, error);
        await this.waitResubscribe();
        continue;
      }

      try {
        while (!subscription.stopped) {
          const message = await queue.recv();
          if (subscription.stopped) break;
          if (!message) {
            this.onAudioDiagnostic({ type: "audio_subscription_ended", channel });
            break;
          }
          this.deliverAudioPacket(message);
          let queued;
          while ((queued = queue.tryRecv())) {
            this.deliverAudioPacket(queued);
          }
        }
      } catch (error) {
        if (!subscription.stopped) {
          this.onAudioDiagnostic({ type: "audio_subscription_error", channel });
          this.onWarning(`audio subscription ${channel}`, error);
        }
      }

      if (subscription.stopped) return;
      this.onAudioDiagnostic({ type: "audio_subscription_reconnecting", channel });
      this.client.unsubscribe(this.tenant, `${channel}.#`).catch(this.onWarning);
      await this.waitResubscribe();
    }
  }

  stop() {
    this.sync(this.subscriptions, new Set(), () => {});
  }
}
